/* eslint-disable @typescript-eslint/no-non-null-assertion -- outsmart compiler */
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import {
  useSearchParams,
  useNavigate,
  useLocation,
  createSearchParams,
  createPath,
} from 'react-router';
import type { FlowStep, Params, PathSpec } from './flow-steps';
import { FlowGraph } from './flow-graph';
import { buildGraph } from './build-graph';
import { findStep } from './find-step';

export interface Flow<State> {
  id: string;
  params?: Params;
  goBack?: (params?: Params) => void;
  goNext: (options?: { params?: Params; state?: State }) => void;
  goStep: (id: string, params?: Params) => void;
  complete: () => void;
  close: () => void;
}

const context = createContext<Flow<unknown> | null>(null);

export const flowContext = context;

export function useFlow<State = unknown>(): Flow<State> {
  const ctx = useContext(context);
  if (ctx === null) {
    throw new Error('⚠️ Not in a flow');
  }
  return ctx;
}

function buildPath(path: string, params: Params = {}): string {
  const ensuredParams = Object.fromEntries(
    Object.entries(params).filter(([, val]) => val !== undefined) as [string, string][]
  );
  const search = createSearchParams(ensuredParams).toString();
  return createPath({
    pathname: path,
    search,
  });
}

export interface FlowProviderProps<State> {
  children: React.ReactNode;
  steps: Record<string, FlowStep<State>>;
  basePath: `/${string}`;
  entryStep: PathSpec;
  state: State;
  fromFallback?: string; // Default URL to navigate to when closing the flow
  onComplete?: (from: string) => void;
  onClose?: (from: string) => void;
}

export function FlowProvider<State>({
  children,
  steps,
  basePath,
  entryStep,
  state,
  fromFallback = '/',
  onComplete,
  onClose,
}: FlowProviderProps<State>): React.JSX.Element | null {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only needs to run on mount
  const from = useMemo(() => searchParams.get('from') ?? fromFallback, []);

  const graph = new FlowGraph();

  buildGraph({ graph, spec: entryStep, steps, state, basePath });

  const currentNode = graph.getNode(pathname);

  const entryPath = basePath + findStep({ id: entryStep.id, steps }).path(entryStep.params ?? {});
  const entryNode = graph.getNode(entryPath);
  const lastNode = entryNode === undefined ? null : graph.lastNode(entryNode);

  useEffect(() => {
    // Navigate to the last eligible step when user strays from the path
    if (currentNode === undefined && lastNode !== null) {
      // Preserve existing query params when redirecting to last eligible step
      const path =
        searchParams.size > 0
          ? buildPath(lastNode.path, Object.fromEntries(searchParams.entries()))
          : lastNode.path;
      void navigate(path, { replace: true });
    }
  }, [currentNode, lastNode, navigate, pathname, searchParams]);

  const goBack = useCallback(
    (params?: Params): void => {
      const prev = currentNode!.prev;
      if (prev !== null) {
        const path = buildPath(prev.path, params);
        void navigate(path);
      }
    },
    [currentNode, navigate]
  );

  const goNext = useCallback(
    (options: { params?: Params; state?: State } = {}): void => {
      const newGraph = new FlowGraph();
      buildGraph({
        graph: newGraph,
        spec: entryStep,
        steps,
        state: options.state ?? state,
        basePath,
      });
      const node = newGraph.getNode(pathname)!;
      if (node.next !== null) {
        const path = buildPath(node.next.path, options.params);
        void navigate(path);
      }
    },
    [basePath, entryStep, navigate, pathname, state, steps]
  );

  const goStep = useCallback(
    (id: string, params: Params = {}): void => {
      const step = findStep({ id, steps });
      const basePlusPath = basePath + step.path(params);
      const path = buildPath(basePlusPath, params);

      void navigate(path);
    },
    [basePath, navigate, steps]
  );

  const close = useCallback((): void => {
    if (onClose) {
      onClose(from);
    } else {
      void navigate(from);
    }
  }, [from, navigate, onClose]);

  const complete = useCallback((): void => {
    if (onComplete) {
      onComplete(from);
    } else {
      void navigate(from);
    }
  }, [from, navigate, onComplete]);

  const contextValue = useMemo(
    () =>
      ({
        id: currentNode?.id,
        params: currentNode?.params,
        goNext,
        goBack: currentNode?.prev === null ? undefined : goBack,
        goStep,
        complete,
        close,
      }) as Flow<unknown>,
    [currentNode, goNext, goBack, goStep, complete, close]
  );

  if (currentNode === undefined) {
    return null;
  }

  return <context.Provider value={contextValue}>{children}</context.Provider>;
}
